import React, {useEffect, useState} from "react";
import {Alert, Drawer, Input, Modal, Spin, message} from "antd";
import {ApiCheckerboardService} from "../../../../../shared/api";
import {ApartmentEditor} from "../ApartmentsSection/ApartmentEditor";
import {authModel} from "../../../../../entities/auth";
import {useCanEdit} from "../../../sectionAccess";

const STATUS_CLASS = {'Свободно': 'free', 'Забронировано': 'booked', 'Продано': 'sold', 'Скрыта': 'hidden'}
const money = (value) => (value ? `${Math.round(Number(value) / 1000).toLocaleString('ru-RU')} т.₽` : '')

// the board of one building, as agents see it: floors top to bottom, stands left to right; a flat opens its editor
// (with «Квартиры» in the user's roles), a stand's name can be changed («Шахматка: правка»)
export const Board = ({building, complexes, onClose}) => {
  const canEdit = useCanEdit()
  const canOpenFlats = authModel.canView('apartments')
  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [openFlat, setOpenFlat] = useState(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    ApiCheckerboardService.fetchBoard(building.id)
      .then(({data}) => alive && setBoard(data))
      .catch(() => alive && setError('Не удалось загрузить шахматку.'))
    return () => {
      alive = false
    }
  }, [building.id, tick])

  const reload = () => setTick((n) => n + 1)

  const renameStand = (section, stand) => {
    let value = stand.alternativeName || ''
    Modal.confirm({
      title: `Подпись стояка ${stand.numberOnFloor} (секция ${section.name})`,
      content: (
        <>
          <p className='cell-hint'>Так стояк подписан над шахматкой у агентов. Пусто — номер стояка.</p>
          <Input defaultValue={value} maxLength={50} onChange={(e) => { value = e.target.value }}/>
        </>
      ),
      okText: 'Сохранить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await ApiCheckerboardService.renameStand(section.id, stand.apartments.map((a) => a.id), value)
          message.success('Сохранено')
          reload()
        } catch (e) {
          message.error(e?.response?.data?.errors?.message || 'Не удалось сохранить')
          throw e
        }
      },
    })
  }

  const sections = board ? board.sections || [] : []
  const floors = Math.max(0, ...sections.map((s) => Number(s.totalFloors) || 0))
  const counts = {}
  sections.forEach((s) => [...s.stand].forEach((st) => st.apartments.forEach((a) => {
    counts[a.statuses || '—'] = (counts[a.statuses || '—'] || 0) + 1
  })))

  return (
    <Drawer visible width={Math.min(1400, window.innerWidth)} title={`Корпус ${building.number} · шахматка`} onClose={onClose} destroyOnClose>
      {error && <Alert type='error' message={error}/>}
      {!error && !board && <div className='complex-editor__loading'><Spin/></div>}
      {board && !sections.length && <Alert type='info' message='В корпусе нет квартир.'/>}
      {board && sections.length > 0 && (
        <>
          <div className='cb-legend'>
            {Object.entries(counts).map(([status, n]) => (
              <span key={status} className={`cb-legend__item cb-flat--${STATUS_CLASS[status] || 'other'}`}>{status}: {n}</span>
            ))}
            <span className='cell-hint'>Квартира открывается по клику; подпись стояка — по клику на его номер.</span>
          </div>
          <div className='cb-board'>
            <div className='cb-floors'>
              <div className='cb-floors__head'/>
              {Array.from({length: floors}, (v, i) => floors - i).map((floor) => <div key={floor} className='cb-floors__floor'>{floor}</div>)}
            </div>
            {sections.map((section) => {
              const stands = [...section.stand]
              const columns = Array.from({length: Number(section.maximumInSection) || stands.length}, (v, i) => i + 1)
              return (
                <div key={section.id} className='cb-section'>
                  <div className='cb-section__title'>Секция {section.name}</div>
                  <div className='cb-section__grid' style={{gridTemplateColumns: `repeat(${columns.length}, 104px)`}}>
                    {columns.map((n) => {
                      const stand = stands.find((s) => String(s.numberOnFloor) === String(n))
                      return (
                        <button
                          key={`h${n}`}
                          type='button'
                          className='cb-stand'
                          disabled={!stand || !canEdit}
                          onClick={() => stand && canEdit && renameStand(section, stand)}
                          title={!stand ? 'Пустой стояк' : canEdit ? 'Изменить подпись стояка' : ''}
                        >
                          {(stand && stand.alternativeName) || n}
                        </button>
                      )
                    })}
                    {Array.from({length: floors}, (v, i) => floors - i).flatMap((floor) => columns.map((n) => {
                      const stand = stands.find((s) => String(s.numberOnFloor) === String(n))
                      const flat = stand && stand.apartments.find((a) => Number(a.floor) === floor)
                      if (floor > (Number(section.totalFloors) || floors)) return <div key={`${floor}-${n}`} className='cb-cell cb-cell--none'/>
                      if (!flat) return <div key={`${floor}-${n}`} className='cb-cell'/>
                      return (
                        <button
                          key={`${floor}-${n}`}
                          type='button'
                          className={`cb-cell cb-flat cb-flat--${STATUS_CLASS[flat.statuses] || 'other'}`}
                          onClick={() => canOpenFlats && setOpenFlat(flat.id)}
                          disabled={!canOpenFlats}
                          title={`ID ${flat.id}${flat.statuses ? ` · ${flat.statuses}` : ''}`}
                        >
                          <b>{flat.houseNumber ? `№ ${flat.houseNumber}` : `ID ${flat.id}`}</b>
                          <span>{[flat.numberOfRooms, (flat.reducedArea || flat.totalSquare) && `${flat.reducedArea || flat.totalSquare} м²`].filter(Boolean).join(' · ')}</span>
                          <span>{money(flat.fullPrice || flat.actualPrice || flat.marketPrice)}</span>
                        </button>
                      )
                    }))}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
      {openFlat && (
        <ApartmentEditor
          key={openFlat}
          apartmentId={openFlat}
          complexes={complexes}
          readOnly={!authModel.canEdit('apartments')}
          onClose={() => setOpenFlat(null)}
          onSaved={reload}
          onCreated={() => {}}
          onDeleted={() => {
            setOpenFlat(null)
            reload()
          }}
        />
      )}
    </Drawer>
  )
}
